import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';

import { tenantsQueryKey } from './useTenants';

/**
 * SLICE 55 — rename a tenant's ACCOUNT NAME (the customer-facing name shown on the
 * tenant list and in the portal header). Super-admin only.
 *
 * Mirrors the wire contract in `api/src/modules/admin/*` (seam S3):
 *   PATCH /api/admin/tenants/:tenantId/name   body { name }
 *     200 { tenantId, name }                  — renamed (or a no-op same-name)
 *     400 { error: ... }                      — empty / whitespace / too long
 *     404 { error: 'not_found' }              — malformed uuid OR unknown tenant
 *
 * A 200 with a JSON body, so `apiJson` (NOT `apiVoid` — the 17x defect was the
 * opposite direction: parsing an empty 204 body). On success the tenant list is
 * invalidated so the row (and every dialog reading `tenant.name`) reflects the new
 * name without a manual refetch.
 *
 * 🔴 The Traccar group is deliberately NOT renamed here — it is internal, mapped by
 * id, and never customer-visible (DESIGN D). This changes the `tenants.name` row
 * only.
 */

export type EditTenantNameFailure = 'not_found' | 'invalid_body' | 'unknown';

export function classifyEditTenantNameError(err: unknown): EditTenantNameFailure {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'not_found';
    if (err.status === 400) return 'invalid_body';
  }
  return 'unknown';
}

export interface EditTenantNameVars {
  tenantId: string;
  /** The name exactly as the operator typed it; the server trims and validates. */
  name: string;
}

export interface EditTenantNameResult {
  tenantId: string;
  name: string;
}

export function useEditTenantName(): UseMutationResult<
  EditTenantNameResult,
  Error,
  EditTenantNameVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, name }: EditTenantNameVars) =>
      apiJson<EditTenantNameResult>(
        `/api/admin/tenants/${encodeURIComponent(tenantId)}/name`,
        {
          method: 'PATCH',
          body: JSON.stringify({ name }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}
